import React from "react";
import { Link } from "react-router-dom";
import missionPattern from "../assets/about-modern/mission.svg";
import valuesGlow from "../assets/about-modern/values.svg";
import showcaseCommunity from "../assets/about/graduation-high-school-university-concept.jpg";
import showcaseLearning from "../assets/about/kid-with-vr-glasses-experiencing-metaverse.jpg";
import showcasePlatform from "../assets/about/man-woman-celebrating-their-wedding-beach.jpg";
import { useTranslation } from "../context/I18nContext";
import "./About.css";

function splitContent(item) {
  if (!item) return { title: null, description: "" };
  const { title, description } = item;
  return { title: title || null, description: description || "" };
}

export default function About() {
  const { t, language } = useTranslation();
  const dir = language === "ar" ? "rtl" : "ltr";

  const asArray = (value) => (Array.isArray(value) ? value : []);

  const heroParagraphs = asArray(
    t("aboutPage.hero.story", { returnObjects: true }),
  );
  const heroHighlights = asArray(
    t("aboutPage.hero.highlights", { returnObjects: true }),
  );
  const missionItems = asArray(
    t("aboutPage.mission.items", { returnObjects: true }),
  );
  const resourceItems = asArray(
    t("aboutPage.resources.items", { returnObjects: true }),
  );
  const valueItems = asArray(
    t("aboutPage.values.items", { returnObjects: true }),
  );
  const visionPoints = asArray(
    t("aboutPage.vision.points", { returnObjects: true }),
  );
  const showcaseItemsRaw = asArray(
    t("aboutPage.showcase.cards", { returnObjects: true }),
  );
  const showcaseMedia = [showcasePlatform, showcaseLearning, showcaseCommunity];
  const showcaseItems = (showcaseItemsRaw || []).map((item, index) => ({
    ...item,
    image: showcaseMedia[index] || showcaseCommunity,
  }));

  return (
    <main className="about-page" dir={dir}>
      <section className="about-hero">
        <div className="about-hero__glow" aria-hidden />
        <div className="container about-hero__body">
          <div className="about-hero__text">
            <span className="section-label">{t("aboutPage.hero.label")}</span>
            <h1>{t("aboutPage.hero.title")}</h1>
            {heroParagraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            <div className="about-hero__grid">
              {heroHighlights.map((item) => (
                <article className="about-hero__card" key={item.title}>
                  <span className="about-hero__icon" aria-hidden>
                    {item.icon}
                  </span>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              ))}
            </div>
          </div>
          <div className="about-hero__figure">
            <div className="about-hero__figure-frame">
              <div className="about-hero__videoFrame">
                <iframe
                  className="about-hero__video"
                  src="https://www.youtube.com/embed/rPzxuscN2I0?autoplay=1&mute=1&loop=1&playlist=rPzxuscN2I0&controls=0&modestbranding=1&playsinline=1&iv_load_policy=3&rel=0&autohide=1&fs=0&disablekb=1"
                  title="Casaclass video"
                  frameBorder="0"
                  allow="autoplay; fullscreen; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="about-hero__tokens" aria-hidden>
                <span className="about-hero__token about-hero__token--sun">
                  ☀️
                </span>
                <span className="about-hero__token about-hero__token--book">
                  📖
                </span>
                <span className="about-hero__token about-hero__token--star">
                  ⭐
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-section about-section--with-art" id="mission">
        <div className="about-section__art mission-art" aria-hidden>
          <img src={missionPattern} alt="" />
        </div>
        <div className="container">
          <div className="section-header ">
            <span className="section-label">
              {t("aboutPage.mission.label")}
            </span>
            <h2>{t("aboutPage.mission.title")}</h2>
            <p>{t("aboutPage.mission.description")}</p>
          </div>
          <div className="about-grid about-grid--text">
            {missionItems.map((item) => {
              const { title, description } = splitContent(item);
              return (
                <article className="about-card" key={title || description}>
                  {title ? <h3>{title}</h3> : null}
                  <p>{description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="about-section" id="resources">
        <div className="container">
          <div className="section-header ">
            <span className="section-label">
              {t("aboutPage.resources.label")}
            </span>
            <h2>{t("aboutPage.resources.title")}</h2>
            <p>{t("aboutPage.resources.description")}</p>
          </div>
          <div className="about-grid about-grid--text">
            {resourceItems.map((item) => {
              const { title, description } = splitContent(item);
              return (
                <article
                  className="about-card about-card--soft"
                  key={title || description}
                >
                  {title ? <h3>{title}</h3> : null}
                  <p>{description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="about-section about-section--with-glow" id="values">
        <div className="about-section__glow values-glow" aria-hidden>
          <img src={valuesGlow} alt="" />
        </div>
        <div className="container">
          <div className="section-header">
            <span className="section-label">{t("aboutPage.values.label")}</span>
            <h2>{t("aboutPage.values.title")}</h2>
            <p>{t("aboutPage.values.description")}</p>
          </div>
          <div className="values-grid">
            {valueItems.map((item) => {
              const { title, description } = splitContent(item);
              return (
                <article className="value-card" key={title || description}>
                  {title ? <h3>{title}</h3> : null}
                  <p>{description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="about-section about-vision" id="vision">
        <div className="about-vision__shell">
          <div className="about-vision__body">
            <div className="about-vision__intro">
              <span className="section-label1">
                {t("aboutPage.vision.label")}
              </span>
              <h2>{t("aboutPage.vision.title")}</h2>
              <p className="about-vision__preamble">
                {t("aboutPage.vision.description")}
              </p>
              <p>{t("aboutPage.vision.intro")}</p>
            </div>
            <ul className="about-vision__list">
              {visionPoints.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section
        className="about-section about-showcase"
        aria-label="مشاهد من Casaclass"
      >
        <div className="container">
          <div className="section-header">
            <span className="section-label">
              {t("aboutPage.showcase.label")}
            </span>
            <h2>{t("aboutPage.showcase.title")}</h2>
            <p>{t("aboutPage.showcase.description")}</p>
          </div>
          <div className="showcase-grid">
            {showcaseItems.map((item) => (
              <article className="showcase-card" key={item.title}>
                <div className="showcase-figure">
                  <img src={item.image} alt={item.alt} />
                </div>
                <div className="showcase-copy">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about-cta" id="rejoindre">
        <div className="container about-cta__wrapper">
          <div>
            <h2>{t("aboutPage.cta.title")}</h2>
            <p>{t("aboutPage.cta.description")}</p>
          </div>
          <div className="about-cta__actions">
            <Link to="/contact" className="btn secondary">
              {t("aboutPage.cta.primary")}
            </Link>
            <Link to="/signup" className="btn ghost" style={{ color: "black" }}>
              {t("aboutPage.cta.secondary")}
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
